import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, X, FileSpreadsheet, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Banner';
import { Badge } from '@/components/ui/StatusBadge';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';
import { useAuthStore } from '@/stores/auth';
import {
  importProspects,
  type ImportReport,
  type ImportError,
  type ImportDuplicate,
} from '@/services/import.api';
import { ApiFailure } from '@/services/api';
import { cn } from '@/lib/utils';

/**
 * Modale d'import CSV/Excel - réservée direction/admin (le bouton qui la
 * déclenche est conditionnel côté parent).
 *
 * Étapes UX :
 *  1. Sélection : drag-drop ou picker, .csv + .xlsx uniquement.
 *  2. Upload : barre de progression (fetch natif ne donne pas de progress
 *     événement, on affiche un indicateur indéterminé).
 *  3. Rapport : résumé + tableaux doublons/erreurs + bouton vers la liste
 *     filtrée sur les prospects importés.
 */
interface Props {
  onClose: () => void;
  /** Callback si l'import a créé ≥ 1 prospect (permet au parent de rafraîchir). */
  onImported?: (report: ImportReport) => void;
}

const ALLOWED_EXT = ['csv', 'xlsx'] as const;

export function ImportProspectsModal({ onClose, onImported }: Props) {
  const token = useAuthStore((s) => s.accessToken)!;
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(true, dialogRef);
  useHotkey('escape', onClose, { allowInInput: true });

  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  function pickFile(f: File | null) {
    if (!f) return;
    const ext = f.name.toLowerCase().split('.').pop() ?? '';
    if (!(ALLOWED_EXT as ReadonlyArray<string>).includes(ext)) {
      setError(`Extension non supportée : .${ext} (attendu : .csv ou .xlsx)`);
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError('Fichier trop volumineux (max 5 Mo)');
      return;
    }
    setError(null);
    setFile(f);
    setReport(null);
  }

  async function submit() {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const res = await importProspects(token, file);
      setReport(res);
      if (res.imported > 0 && onImported) onImported(res);
    } catch (err) {
      if (err instanceof ApiFailure) {
        if (err.status === 429) setError('Trop de tentatives - réessayez dans 5 minutes.');
        else if (err.status === 413) setError('Fichier trop volumineux (max 5 Mo).');
        else setError(err.message);
      } else {
        setError('Erreur réseau - réessayez.');
      }
    } finally {
      setUploading(false);
    }
  }

  function viewImported() {
    if (!report || report.importedProspectIds.length === 0) return;
    // V1 : on navigue vers la liste globale. Un filtre `?ids=` nécessiterait
    // une API backend dédiée - reporté V2.
    onClose();
    navigate('/prospects');
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm animate-fade-in"
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Importer des prospects"
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-modal animate-scale-in"
      >
        <div className="flex items-center justify-between border-b border-border-subtle px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-brand">Importer des prospects</h2>
            <p className="mt-0.5 text-xs text-text-muted">CSV ou Excel - max 5 Mo, 5000 lignes.</p>
            <details className="mt-1 text-xs text-text-muted">
              <summary className="cursor-pointer text-accent hover:underline">
                Colonnes acceptées
              </summary>
              <div className="mt-2 space-y-2 text-[11px]">
                <div>
                  <span className="font-semibold text-brand">Discriminator :</span>{' '}
                  <code>type_prospect</code> (<em>entreprise</em> ou <em>particulier</em>). Si la
                  colonne est absente, toutes les lignes sont traitées comme des entreprises
                  (rétrocompat).
                </div>
                <div>
                  <span className="font-semibold text-brand">Entreprise :</span> <code>siret</code>{' '}
                  (14 chiffres, obligatoire), <code>raison_sociale</code> (obligatoire),{' '}
                  <code>adresse</code>, <code>code_postal</code>, <code>ville</code>,{' '}
                  <code>telephone</code>, <code>email</code>, <code>contact_nom</code>,{' '}
                  <code>contact_prenom</code>, <code>contact_email</code>,{' '}
                  <code>contact_telephone</code>.
                </div>
                <div>
                  <span className="font-semibold text-brand">Particulier :</span>{' '}
                  <code>civilite</code> (M/Mme, obligatoire), <code>prenom</code> (obligatoire),{' '}
                  <code>nom</code> (obligatoire), <code>date_naissance</code> (JJ/MM/AAAA ou
                  AAAA-MM-DD, obligatoire), <code>email_perso</code>, <code>telephone_perso</code>,{' '}
                  <code>ppe</code> (oui/non, défaut non), <code>adresse</code>,{' '}
                  <code>code_postal</code>, <code>ville</code>.
                </div>
                <div>
                  <span className="font-semibold text-brand">Commun :</span>{' '}
                  <code>observations</code>, <code>external_id</code>.
                </div>
                <div>
                  <span className="font-semibold text-brand">Migration depuis ancien CRM :</span>{' '}
                  <code>responsable_email</code> (email d&apos;un utilisateur actif avec équipe -
                  porte <em>assignedUser</em> et <em>createdBy</em>),{' '}
                  <code>date_creation</code> (JJ/MM/AAAA ou AAAA-MM-DD, dans le passé - remplace la
                  date de création automatique). Si absents → l&apos;import vous attribue les
                  prospects à la date du jour comme avant.
                </div>
              </div>
            </details>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-text-muted hover:bg-gray-100 hover:text-brand"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!report && (
            <FileDropZone
              file={file}
              dragOver={dragOver}
              onDragOver={setDragOver}
              onFile={pickFile}
              disabled={uploading}
            />
          )}

          {error && <Banner tone="danger">{error}</Banner>}

          {report && <ReportView report={report} />}

          <div className="flex justify-end gap-2 border-t border-border-subtle pt-4">
            {!report ? (
              <>
                <Button variant="secondary" onClick={onClose} disabled={uploading}>
                  Annuler
                </Button>
                <Button
                  variant="primary"
                  leadingIcon={<Upload className="h-4 w-4" />}
                  disabled={!file}
                  loading={uploading}
                  onClick={() => void submit()}
                >
                  Lancer l&apos;import
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={onClose}>
                  Fermer
                </Button>
                <Button
                  variant="primary"
                  onClick={viewImported}
                  disabled={report.importedProspectIds.length === 0}
                >
                  Voir les prospects importés
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// File drop-zone
// ---------------------------------------------------------------------------

function FileDropZone({
  file,
  dragOver,
  onDragOver,
  onFile,
  disabled,
}: {
  file: File | null;
  dragOver: boolean;
  onDragOver: (v: boolean) => void;
  onFile: (f: File | null) => void;
  disabled: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <label
      htmlFor="import-file"
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-8 transition-colors',
        dragOver ? 'border-accent bg-accent/5' : 'border-border-subtle bg-gray-50',
        disabled && 'pointer-events-none opacity-60',
      )}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(true);
      }}
      onDragLeave={() => onDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        onDragOver(false);
        onFile(e.dataTransfer.files?.[0] ?? null);
      }}
    >
      <FileSpreadsheet className="h-8 w-8 text-text-muted" />
      {file ? (
        <>
          <span className="text-sm font-medium text-brand">{file.name}</span>
          <span className="text-xs text-text-muted">
            {(file.size / 1024).toFixed(1)} Ko -{' '}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onFile(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
              className="text-accent hover:underline"
            >
              changer
            </button>
          </span>
        </>
      ) : (
        <>
          <span className="text-sm font-medium text-brand">
            Glissez un fichier ici ou cliquez pour sélectionner
          </span>
          <span className="text-xs text-text-muted">.csv ou .xlsx · max 5 Mo</span>
        </>
      )}
      <input
        ref={inputRef}
        id="import-file"
        type="file"
        accept=".csv,.xlsx"
        className="sr-only"
        onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        disabled={disabled}
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Report
// ---------------------------------------------------------------------------

function ReportView({ report }: { report: ImportReport }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard
          icon={<Check className="h-4 w-4 text-success-600" />}
          label="Importés"
          value={report.imported}
          tone="success"
        />
        <SummaryCard
          icon={<AlertTriangle className="h-4 w-4 text-warning-600" />}
          label="Ignorés (doublons)"
          value={report.skipped}
          tone="warning"
        />
        <SummaryCard
          icon={<X className="h-4 w-4 text-danger-600" />}
          label="Erreurs"
          value={report.errors.length}
          tone="danger"
        />
      </div>

      <div className="text-xs text-text-muted">Total lignes traitées : {report.totalLines}</div>

      {report.duplicates.length > 0 && <DuplicatesTable rows={report.duplicates} />}
      {report.errors.length > 0 && <ErrorsTable rows={report.errors} />}
      {report.warnings.length > 0 && (
        <Banner tone="warning">
          {report.warnings.length} avertissement(s) de parsing (voir console).
        </Banner>
      )}
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone: 'success' | 'warning' | 'danger';
}) {
  const tones: Record<typeof tone, string> = {
    success: 'bg-success-50 border-success-100',
    warning: 'bg-warning-50 border-warning-100',
    danger: 'bg-danger-50 border-danger-100',
  };
  return (
    <div className={cn('rounded-md border p-3', tones[tone])}>
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-xs text-text-muted">{label}</span>
      </div>
      <div className="mt-1 text-2xl font-semibold text-brand">{value}</div>
    </div>
  );
}

function DuplicatesTable({ rows }: { rows: ImportDuplicate[] }) {
  const columns: Column<ImportDuplicate>[] = [
    { key: 'line', header: 'Ligne', width: '60px', align: 'center' },
    {
      key: 'type',
      header: 'Type',
      width: '90px',
      render: (r) => (
        <Badge tone="neutral">{r.type === 'individual' ? 'Particulier' : 'Entreprise'}</Badge>
      ),
    },
    {
      key: 'identity',
      header: 'Identité',
      render: (r) => (
        <div>
          <div className="font-medium text-brand">{r.identity}</div>
          {r.siret && <div className="font-mono text-[11px] text-text-muted">{r.siret}</div>}
        </div>
      ),
    },
    {
      key: 'assignedTo',
      header: 'Déjà assigné à',
      render: (r) =>
        r.existingProspectId === 'in-file' ? (
          <Badge tone="neutral">Doublon dans le fichier</Badge>
        ) : r.assignedTo ? (
          `${r.assignedTo.firstName} ${r.assignedTo.lastName}`
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
  ];
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-brand">Doublons ignorés ({rows.length})</h3>
      <DataTable
        columns={columns}
        data={rows}
        keyExtractor={(r) => `${r.line}:${r.siret ?? r.identity}`}
      />
    </div>
  );
}

function ErrorsTable({ rows }: { rows: ImportError[] }) {
  const columns: Column<ImportError>[] = [
    { key: 'line', header: 'Ligne', width: '60px', align: 'center' },
    { key: 'field', header: 'Champ', width: '120px' },
    { key: 'message', header: 'Message' },
  ];
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-brand">
        Erreurs de validation ({rows.length})
      </h3>
      <DataTable
        columns={columns}
        data={rows}
        keyExtractor={(r) => `${r.line}:${r.field}:${r.message}`}
      />
    </div>
  );
}
